/**
 * Column skins of world 1 (GDD §10.3, §5.4, issue #69). A gap column is two AABBs of the core (#35); a
 * skin dresses a piece so that **what collides is what is seen** (GDD §14.1): the bounding box of the
 * built group equals the piece AABB and every decorative part stays inside it.
 *
 * This module is pure geometry (no Three.js): `columnPieceParts` returns the list of primitives in
 * piece-local coordinates, so it can be unit-tested in Node (#69). `buildColumnPiece` turns them into a
 * Three.js `Group` and lives in `level-scene`'s side of the code.
 */

/** A primitive of a skin, in coordinates local to the centre of the column piece (u). */
export interface SkinPart {
  readonly shape: 'box' | 'cone';
  readonly x: number;
  readonly y: number;
  /** Pushed towards the camera (z > 0) so relief reads; depth is not part of the collision. */
  readonly z: number;
  /** Full width; for a cone, the full base diameter. */
  readonly w: number;
  readonly h: number;
  readonly d: number;
  readonly color: number;
  /** A glowing part (a neon sign, #103): it keeps its colour in the dark. */
  readonly emissive?: boolean;
}

/** Palette of the world 1 skins (game art, GDD §14.2, issue #68): saturated over the decoration. */
export const W1_SKIN_COLORS = {
  towerBody: 0x1f8a2e,
  towerBand: 0x33b84a,
  towerDark: 0x146b22,
  rockBody: 0x2f7d4a,
  rockDark: 0x1f5c34,
  house: 0xb5582f,
  houseDark: 0x8f4326,
  roof: 0xc23a1a,
  fallback: 0x1f8a2e,
} as const;

/**
 * Palette of the world 2 skins (game art, GDD §14.2, issue #102/#103): every tint stays more saturated than the
 * world 2 decoration in every variant (0.697) and the bodies keep 3:1 or more against the sky of their mission.
 */
export const W2_SKIN_COLORS = {
  // w2-containers: stacked containers of the port, deep green with orange door bars.
  containerBody: 0x0f5f2f,
  containerRib: 0x0f8a3f,
  containerDoor: 0xe08000,
  // w2-neon: a night building with lit windows and pink and blue neon signs (emissive).
  neonBody: 0x1f9a4e,
  neonWindow: 0xf2b000,
  neonPink: 0xff2d95,
  neonBlue: 0x2d9bff,
  // w2-stables: a stable of the Valle with a palm trunk and a crown of leaves.
  stableBody: 0x33d060,
  stableBand: 0x1fb04a,
  stableRoof: 0xf2b000,
  palmLeaf: 0x0fbf2f,
} as const;

/**
 * Palette of the world 3 skins (game art, GDD §14.2, issue #114/#115): every tint stays more saturated than
 * the world 3 decoration in its brightest variant (`tarde`, 0.563), so the columns always read as obstacles.
 */
export const W3_SKIN_COLORS = {
  // w3-silos: teal metal silos and windmill towers.
  siloBody: 0x1f9a6a,
  siloBand: 0x2fbf6a,
  siloDark: 0x167a52,
  blades: 0x2fd98a,
  // w3-agave: vivid agave rows and church towers.
  agaveBody: 0x0fbf2f,
  agaveLight: 0x2fd93a,
  agaveSpike: 0x1f8a1f,
  church: 0xd9cdb3,
  // w3-colonial: terracotta colonial buildings.
  colBody: 0xc0441f,
  colDark: 0x9a3517,
  colRoof: 0xd94a1a,
  colWindow: 0xd9a520,
} as const;

/**
 * Palette of the world 4 skins (game art, GDD §14.2, issue #125/#126): every tint stays more saturated than
 * the world 4 decoration in its brightest variant (`manana`, 0.488), so the columns always read as obstacles.
 */
export const W4_SKIN_COLORS = {
  // w4-sierra-cliff: brown-green Sierra rock faces.
  cliffBody: 0x1f9a4e,
  cliffRock: 0x2fbf5a,
  cliffDark: 0x147a3a,
  // w4-culiacan-chapel: a chapel, neutral and generic (no real sanctuary, §3.4).
  chapelBody: 0x2fae5a,
  chapelRoof: 0x33c04a,
  chapelCross: 0xd9a520,
  // w4-culiacan-block: a Culiacán city building with amber windows.
  blockBody: 0x1fae3e,
  blockWin: 0xe0a000,
  // w4-border-wall: the border wall segments (neutral scenery, no political message).
  wallBody: 0x2f9a4e,
  wallTop: 0x33c04a,
} as const;

/** The z of the relief, in front of the collision body (fraction of the piece depth). */
const RELIEF_Z = 0.06;

const box = (x: number, y: number, z: number, w: number, h: number, d: number, color: number): SkinPart => ({
  shape: 'box',
  x,
  y,
  z,
  w,
  h,
  d,
  color,
});

/**
 * The primitives of one column piece: a solid body that fills the AABB plus relief that stays inside it.
 * `halfW`/`halfH` are the half extents of the piece from the core; `depth` is the visual depth in z.
 */
export function columnPieceParts(skin: string, halfW: number, halfH: number, depth: number, fallbackColor: number = W1_SKIN_COLORS.fallback): readonly SkinPart[] {
  const halfD = depth / 2;
  // Every skin starts with a body that exactly fills the collision box, so the silhouette is solid.
  const body = (color: number) => box(0, 0, 0, halfW * 2, halfH * 2, depth, color);

  if (skin === 'w1-power-tower') {
    const parts: SkinPart[] = [body(W1_SKIN_COLORS.towerBody)];
    // Horizontal cross members of the lattice, flush with the sides and pushed towards the camera.
    const bandH = Math.min(0.24, halfH * 0.5);
    for (const at of [-halfH * 0.5, halfH * 0.4]) {
      if (halfH <= bandH) break;
      parts.push(box(0, at, halfD + RELIEF_Z, halfW * 2, bandH, 0.12, W1_SKIN_COLORS.towerBand));
    }
    // Insulator dots down the centre, always inside the piece.
    const dot = Math.min(0.22, halfH * 0.4, halfW * 0.4);
    for (const at of [-halfH * 0.25, halfH * 0.15, halfH * 0.6]) {
      if (Math.abs(at) + dot / 2 > halfH) continue;
      parts.push(box(0, at, halfD + RELIEF_Z, dot, dot, 0.12, W1_SKIN_COLORS.towerDark));
    }
    return parts;
  }

  if (skin === 'w1-rock-houses') {
    const parts: SkinPart[] = [body(W1_SKIN_COLORS.rockBody)];
    // Little brick houses stuck to the rock, with a roof; all inside the collision box.
    const houseW = Math.min(halfW * 1.2, 1.1);
    const houseH = Math.min(halfH * 0.6, 0.7);
    const roofH = 0.24;
    const offsets = [-halfH * 0.45, halfH * 0.25];
    offsets.forEach((at, index) => {
      // Keep the house and its roof inside the piece: a short piece may have room for none.
      if (halfH <= houseH || at - houseH / 2 < -halfH || at + houseH / 2 + roofH > halfH) return;
      const x = index === 0 ? -halfW + houseW / 2 + 0.05 : halfW - houseW / 2 - 0.05;
      parts.push(box(x, at, halfD + RELIEF_Z, houseW, houseH, 0.5, index === 0 ? W1_SKIN_COLORS.house : W1_SKIN_COLORS.houseDark));
      parts.push({ shape: 'cone', x, y: at + houseH / 2 + 0.12, z: halfD + RELIEF_Z, w: houseW * 1.1, h: 0.24, d: 0.5, color: W1_SKIN_COLORS.roof });
    });
    // A darker cleft up the middle, never wider or taller than the piece.
    const cleft = Math.min(0.3, halfW * 0.5);
    parts.push(box(0, 0, halfD + RELIEF_Z, cleft, halfH * 2, 0.12, W1_SKIN_COLORS.rockDark));
    return parts;
  }

  if (skin === 'w2-containers') {
    // A stack of containers: modules of 1 u from the bottom, a dark seam between them, a rib and an orange door bar
    // per module, and the last module cut to close the box exactly. Everything stays inside the AABB.
    const parts: SkinPart[] = [body(W2_SKIN_COLORS.containerBody)];
    const module = 1;
    const seam = 0.08;
    for (let at = -halfH + module; at < halfH - seam; at += module) {
      parts.push(box(0, at, halfD + RELIEF_Z, halfW * 2, seam, 0.1, W2_SKIN_COLORS.containerRib));
    }
    const doorH = Math.min(0.5, halfH * 0.5);
    for (let low = -halfH; low + module <= halfH + 1e-9; low += module) {
      if (halfW * 0.3 > halfW) continue;
      parts.push(box(halfW * 0.5, low + module / 2, halfD + RELIEF_Z, Math.min(0.12, halfW * 0.3), Math.min(doorH, module * 0.6), 0.12, W2_SKIN_COLORS.containerDoor));
    }
    return parts;
  }

  if (skin === 'w2-neon') {
    // A night building: a body with floors of lit windows and two neon bars (pink and blue, emissive) on its face.
    const parts: SkinPart[] = [body(W2_SKIN_COLORS.neonBody)];
    const winW = Math.min(halfW * 0.4, 0.3);
    const winH = Math.min(halfH * 0.25, 0.3);
    for (const at of [-halfH * 0.7, -halfH * 0.2, halfH * 0.3]) {
      if (Math.abs(at) + winH / 2 > halfH) continue;
      for (const side of [-1, 1]) parts.push(box(side * halfW * 0.45, at, halfD + RELIEF_Z, winW, winH, 0.12, W2_SKIN_COLORS.neonWindow));
    }
    const signH = Math.min(0.12, halfH * 0.2);
    for (const [at, color] of [[halfH * 0.7, W2_SKIN_COLORS.neonPink], [-halfH * 0.45, W2_SKIN_COLORS.neonBlue]] as const) {
      if (Math.abs(at) + signH / 2 > halfH) continue;
      parts.push({ ...box(0, at, halfD + RELIEF_Z + 0.04, halfW * 1.7, signH, 0.1, color), emissive: true });
    }
    return parts;
  }

  if (skin === 'w2-stables') {
    // A palm trunk with ring bands, a crown of leaves (cones) at the top and a roof ledge: all inside the box.
    const parts: SkinPart[] = [body(W2_SKIN_COLORS.stableBody)];
    const bandH = Math.min(0.14, halfH * 0.3);
    for (let at = -halfH + 0.8; at < halfH - bandH; at += 0.8) {
      parts.push(box(0, at, halfD + RELIEF_Z, halfW * 1.8, bandH, 0.1, W2_SKIN_COLORS.stableBand));
    }
    const roofH = Math.min(0.22, halfH * 0.4);
    if (halfH > roofH) parts.push(box(0, halfH - roofH / 2, halfD + RELIEF_Z, halfW * 2, roofH, 0.4, W2_SKIN_COLORS.stableRoof));
    const leafW = Math.min(halfW * 0.9, 0.55);
    const leafH = Math.min(halfH * 0.6, 0.7);
    if (halfH > roofH + leafH) {
      for (const side of [-1, 1]) {
        parts.push({ shape: 'cone', x: side * halfW * 0.4, y: halfH - roofH - leafH / 2, z: halfD + RELIEF_Z, w: leafW, h: leafH, d: 0.4, color: W2_SKIN_COLORS.palmLeaf });
      }
    }
    return parts;
  }

  if (skin === 'w3-silos') {
    // Teal metal silo: a body with horizontal bands and a capped top; the windmill blades are decoration.
    const parts: SkinPart[] = [body(W3_SKIN_COLORS.siloBody)];
    const bandH = Math.min(0.22, halfH * 0.4);
    for (const at of [-halfH * 0.6, 0, halfH * 0.55]) {
      if (halfH <= bandH || Math.abs(at) + bandH / 2 > halfH) continue;
      parts.push(box(0, at, halfD + RELIEF_Z, halfW * 1.8, bandH, 0.14, W3_SKIN_COLORS.siloBand));
    }
    // A dark vertical seam down the side, inside the box.
    if (halfW * 0.3 <= halfW) parts.push(box(halfW * 0.55, 0, halfD + RELIEF_Z, Math.min(0.14, halfW * 0.3), halfH * 2, 0.1, W3_SKIN_COLORS.siloDark));
    return parts;
  }

  if (skin === 'w3-agave') {
    // Agave row: a green body with pointed leaves (cones) and a church-tower hint at the top.
    const parts: SkinPart[] = [body(W3_SKIN_COLORS.agaveBody)];
    const leafW = Math.min(halfW * 0.9, 0.5);
    const leafH = Math.min(halfH * 0.9, 0.7);
    for (const at of [-halfH * 0.55, -halfH * 0.1, halfH * 0.35]) {
      if (halfH <= leafH || Math.abs(at) + leafH / 2 > halfH) continue;
      parts.push({ shape: 'cone', x: 0, y: at, z: halfD + RELIEF_Z, w: leafW, h: leafH, d: 0.4, color: at < 0 ? W3_SKIN_COLORS.agaveSpike : W3_SKIN_COLORS.agaveLight });
    }
    const towerW = Math.min(halfW * 0.5, 0.5);
    const towerH = Math.min(halfH * 0.5, 1.2);
    if (halfH > towerH && towerH > 0) parts.push(box(0, halfH - towerH / 2, halfD + RELIEF_Z, towerW, towerH, 0.3, W3_SKIN_COLORS.church));
    return parts;
  }

  if (skin === 'w3-colonial') {
    // Colonial building: terracotta body with a roof ledge and amber windows; all inside the box.
    const parts: SkinPart[] = [body(W3_SKIN_COLORS.colBody)];
    const roofH = Math.min(0.26, halfH * 0.5);
    if (halfH > roofH) parts.push(box(0, halfH - roofH / 2, halfD + RELIEF_Z, halfW * 2, roofH, 0.5, W3_SKIN_COLORS.colRoof));
    const winW = Math.min(halfW * 0.5, 0.35);
    const winH = Math.min(halfH * 0.4, 0.4);
    for (const at of [-halfH * 0.35, halfH * 0.15]) {
      if (Math.abs(at) + winH / 2 > halfH || halfW * 0.5 - 0.1 < winW) continue;
      parts.push(box(0, at, halfD + RELIEF_Z, winW, winH, 0.14, W3_SKIN_COLORS.colWindow));
    }
    // A darker base band at the bottom, inside the box.
    const baseH = Math.min(0.3, halfH * 0.5);
    if (halfH > baseH) parts.push(box(0, -halfH + baseH / 2, halfD + RELIEF_Z, halfW * 1.8, baseH, 0.16, W3_SKIN_COLORS.colDark));
    return parts;
  }

  if (skin === 'w4-sierra-cliff') {
    // A rock face: the body with darker strata and lighter rock ledges, all inside the box.
    const parts: SkinPart[] = [body(W4_SKIN_COLORS.cliffBody)];
    const ledgeH = Math.min(0.2, halfH * 0.4);
    for (const at of [-halfH * 0.55, -halfH * 0.05, halfH * 0.45]) {
      if (halfH <= ledgeH || Math.abs(at) + ledgeH / 2 > halfH) continue;
      parts.push(box(0, at, halfD + RELIEF_Z, halfW * 1.7, ledgeH, 0.16, W4_SKIN_COLORS.cliffRock));
    }
    if (halfW * 0.3 <= halfW) parts.push(box(-halfW * 0.5, 0, halfD + RELIEF_Z, Math.min(0.16, halfW * 0.3), halfH * 2, 0.1, W4_SKIN_COLORS.cliffDark));
    return parts;
  }

  if (skin === 'w4-culiacan-chapel') {
    // A generic chapel: body, a roof ledge and a cross, all inside the box.
    const parts: SkinPart[] = [body(W4_SKIN_COLORS.chapelBody)];
    const roofH = Math.min(0.24, halfH * 0.4);
    if (halfH > roofH) parts.push(box(0, halfH - roofH / 2, halfD + RELIEF_Z, halfW * 2, roofH, 0.4, W4_SKIN_COLORS.chapelRoof));
    const cross = Math.min(0.18, halfW * 0.4, halfH * 0.4);
    if (halfH > cross && halfW * 0.5 > cross / 2) parts.push(box(0, halfH - cross / 2, halfD + RELIEF_Z + 0.05, cross, cross, 0.08, W4_SKIN_COLORS.chapelCross));
    return parts;
  }

  if (skin === 'w4-culiacan-block') {
    // A city block: body with amber windows in two columns, inside the box.
    const parts: SkinPart[] = [body(W4_SKIN_COLORS.blockBody)];
    const winW = Math.min(halfW * 0.5, 0.35);
    const winH = Math.min(halfH * 0.35, 0.35);
    for (const at of [-halfH * 0.35, halfH * 0.15, halfH * 0.55]) {
      if (Math.abs(at) + winH / 2 > halfH || halfW * 0.5 - 0.1 < winW) continue;
      parts.push(box(0, at, halfD + RELIEF_Z, winW, winH, 0.14, W4_SKIN_COLORS.blockWin));
    }
    return parts;
  }

  if (skin === 'w4-border-wall') {
    // A border wall segment: body with a lighter top rail, all inside the box. Neutral scenery.
    const parts: SkinPart[] = [body(W4_SKIN_COLORS.wallBody)];
    const railH = Math.min(0.2, halfH * 0.4);
    if (halfH > railH) parts.push(box(0, halfH - railH / 2, halfD + RELIEF_Z, halfW * 2, railH, 0.2, W4_SKIN_COLORS.wallTop));
    return parts;
  }

  // Unknown skin (`test`, the giraffes of #98, the worlds without art yet): a saturated box that fills the
  // AABB. The caller may pass the theme's collidable tint, so the fallback stays legible against that
  // world's sky (issue #114); by default it is the world 1 column green.
  return [body(fallbackColor)];
}

/** Whether every primitive stays inside the piece AABB in x and y (the collision is 2D). */
export function partsWithinBounds(parts: readonly SkinPart[], halfW: number, halfH: number, tolerance = 0): boolean {
  return parts.every(
    (part) => Math.abs(part.x) + part.w / 2 <= halfW + tolerance && Math.abs(part.y) + part.h / 2 <= halfH + tolerance,
  );
}

/**
 * Opaque coverage (0 to 1) of the piece: the area of the union of the parts over the area of the AABB. A
 * silhouette of 1 fills the whole collision box; lattice decoration that stuck out or left holes would
 * lower it. Exact, apart from floating point, by splitting the box with the part edges.
 */
export function partsCoverage(parts: readonly SkinPart[], halfW: number, halfH: number): number {
  if (halfW <= 0 || halfH <= 0) return 0;
  const xEdges = [...new Set([-halfW, halfW, ...parts.flatMap((part) => [part.x - part.w / 2, part.x + part.w / 2])])].sort((a, b) => a - b);
  const yEdges = [...new Set([-halfH, halfH, ...parts.flatMap((part) => [part.y - part.h / 2, part.y + part.h / 2])])].sort((a, b) => a - b);
  let area = 0;
  for (let i = 0; i < xEdges.length - 1; i++) {
    for (let j = 0; j < yEdges.length - 1; j++) {
      const cx = (xEdges[i]! + xEdges[i + 1]!) / 2;
      const cy = (yEdges[j]! + yEdges[j + 1]!) / 2;
      if (parts.some((part) => Math.abs(cx - part.x) <= part.w / 2 && Math.abs(cy - part.y) <= part.h / 2)) {
        area += (xEdges[i + 1]! - xEdges[i]!) * (yEdges[j + 1]! - yEdges[j]!);
      }
    }
  }
  return area / (halfW * 2 * halfH * 2);
}
